import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";

/**
 * Site language. The choice is remembered in localStorage so a returning
 * visitor lands straight in their language; a first visit follows the
 * browser's language when we have it, and falls back to Romanian.
 */

export const LANGS = ["ro", "en", "de"];
const STORAGE_KEY = "pb-lang";

const isSupported = (l) => LANGS.includes(l);

const initialLang = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch {
    /* storage unavailable (private mode, blocked) — fall through */
  }
  const browser = (navigator.language || "").slice(0, 2).toLowerCase();
  return isSupported(browser) ? browser : "ro";
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(initialLang);

  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* remembering is best-effort */
    }
  }, []);

  // Cycles RO → EN → DE → RO; kept for any caller that still uses it.
  const toggle = useCallback(() => {
    setLang(LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length]);
  }, [lang, setLang]);

  // Screen readers, hyphenation and search engines read this attribute.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, toggle, langs: LANGS, t: translations[lang] }),
    [lang, setLang, toggle],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
